import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'

import cogoToast from 'cogo-toast'
import { orderBy } from 'lodash'
import Moment from 'moment'

import sendRequest from '../../../services/api/sendRequest'
import CasesService from '../../../services/CasesService'
import { MEDICAL_RECORD_SUBTYPE_MAP, MEDICAL_RECORD_SUBTYPES } from '../../../utils/Constants'
import { getToken } from '../../../utils/getToken'
import Permissions, { CAPABILITIES } from '../../../utils/Permissions'
import MagicWand from '../../common/MagicWand'
import FileMetadataModal from '../../controls/FileMetadataModal'
import FAIcon from '../../FAIcon'
import LoadingSpinner from '../../LoadingSpinner'
import CameraScanModal from '../CameraScanModal'
import { applyAcceptedProviders, HitlReviewModal, sanitizeCaseSystemsAndFunctional } from '../HitlReview'

import './CaseDocumentsTab.scss'

const VIEW_MODE_KEY = 'caseDocumentsViewMode'

const MoveDocumentModal = ({ show, onHide, onConfirm, currentCaseNumber }) => {
  const [targetCaseNumber, setTargetCaseNumber] = useState('')
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (show) {
      loadCases()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const loadCases = async () => {
    setLoading(true)
    try {
      // Use correct fields based on Schema (client.firstName/lastName)
      const allCases = await CasesService.getAll(['caseNumber', 'client.firstName', 'client.lastName'])
      // Filter out current case and ensure user has write access (implicit in getAll usually, but can double check if needed)
      // For now, getAll returns what user has access to.
      // We should ideally filter for write access if the API returned permissions, but standard getAll usually implies read.
      // The requirement was: "filter the list to only show cases where the user has write authority".
      // Without a specific "permissions" field on the case model returned by getAll, we might rely on the backend validation or assumption.
      // However, if the user sees it, they can try to move it.

      const otherCases = allCases.filter(c => c.caseNumber !== parseInt(currentCaseNumber))
      // Sort by Name
      otherCases.sort((a, b) => {
        const nameA = (a.client && `${a.client.firstName} ${a.client.lastName}`) || `Case ${a.caseNumber}`
        const nameB = (b.client && `${b.client.firstName} ${b.client.lastName}`) || `Case ${b.caseNumber}`
        return nameA.localeCompare(nameB)
      })

      setCases(otherCases)
    } catch (err) {
      alert('Failed to load potential target cases.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = () => {
    if (!targetCaseNumber) return
    onConfirm(targetCaseNumber)
  }

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Move Document</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center">Loading cases...</div>
        ) : (
          <Form>
            <Form.Group>
              <Form.Label>Select the case you want to move this document to.</Form.Label>
              <Form.Control as="select" value={targetCaseNumber} onChange={e => setTargetCaseNumber(e.target.value)}>
                <option value="">-- Select Case --</option>
                {cases.map(c => {
                  const clientName = c.client ? `${c.client.firstName} ${c.client.lastName}` : 'Unknown Client'
                  return (
                    <option key={c.caseNumber} value={c.caseNumber}>
                      {clientName} (#{c.caseNumber})
                    </option>
                  )
                })}
              </Form.Control>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!targetCaseNumber}>
          Move
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const CustomConfirmModal = ({ show, onHide, onConfirm, title, message, files, confirmText, confirmVariant }) => {
  return (
    <Modal show={show} onHide={onHide} centered className="gs-confirm-modal">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="font-weight-bold" style={{ fontSize: '1.25rem' }}>
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        <p className="text-muted" style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
          {message}
        </p>
        {files && files.length > 0 && (
          <div className="bg-light p-3 rounded mb-3 border" style={{ maxHeight: '150px', overflowY: 'auto' }}>
            <ul className="list-unstyled mb-0 small text-secondary">
              {files.map((f, idx) => (
                <li key={idx} className="text-truncate py-1 border-bottom last-border-0">
                  <FAIcon name="file" className="mr-2 text-muted" />
                  {f.name || f}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="light"
          onClick={onHide}
          className="border px-4 py-2 font-weight-bold"
          style={{ borderRadius: '10px' }}
        >
          Cancel
        </Button>
        <Button
          variant={confirmVariant || 'primary'}
          onClick={onConfirm}
          className="px-4 py-2 font-weight-bold"
          style={{ borderRadius: '10px' }}
        >
          {confirmText || 'Confirm'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const formatDiagnosisItem = d => {
  if (!d) return ''
  if (typeof d === 'string') return d
  const name = d.name || d.description || ''
  const code = d.icd10 || d.code || ''
  const level = d.anatomicalLevel ? ` [${d.anatomicalLevel}]` : ''
  if (code && name && !name.includes(code)) return `${code} - ${name}${level}`
  return (name || code || '') + level
}

const formatProviderItem = p => {
  if (!p) return ''
  if (typeof p === 'string') return p
  const name = p.name || [p.firstName, p.lastName].filter(Boolean).join(' ') || p.practiceName || 'Treating Provider'
  const details = []
  if (p.npi) details.push(`NPI: ${p.npi}`)
  if (p.phone) details.push(`Phone: ${p.phone}`)
  return details.length > 0 ? `${name} (${details.join(', ')})` : name
}

export const formatUserDescription = (rawDesc, currentUser) => {
  if (!rawDesc) return ''
  const guidRegex = /\[Uploaded by:\s*([0-9a-fA-F-]{36})\]/g
  return rawDesc.replace(guidRegex, (match, guid) => {
    if (currentUser) {
      const userGuid = currentUser.id || currentUser.cognitoId || currentUser.sub || ''
      const isMatch = !userGuid || String(userGuid).toLowerCase() === String(guid).toLowerCase()
      const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ')
      if (fullName && (isMatch || !userGuid)) {
        return `[Uploaded by: ${fullName}]`
      }
    }
    return match
  })
}

export const formatUploadedBy = (uploadedBy, currentUser) => {
  if (!uploadedBy) return '-'
  const guidRegex = /^[0-9a-fA-F-]{36}$/
  if (guidRegex.test(uploadedBy.trim())) {
    if (currentUser) {
      const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ')
      if (fullName) return fullName
    }
  }
  return uploadedBy
}

const CaseDocumentsTab = ({ caseNumber, user }) => {
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem(VIEW_MODE_KEY)
    if (saved) return saved
    return window.innerWidth < 768 ? 'grid' : 'list'
  })
  const [documents, setDocuments] = useState([])
  const [docTypeFilter, setDocTypeFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const filteredDocuments = useMemo(() => {
    if (!docTypeFilter || docTypeFilter === 'all') return documents
    return documents.filter(file => {
      let properties = {}
      try {
        if (file.properties) {
          properties = typeof file.properties === 'string' ? JSON.parse(file.properties) : file.properties
        }
      } catch (e) {}

      const purpose = properties['Document Purpose'] || file.documentPurpose || file.type || ''
      const subtype = properties['Document Subtype'] || file.documentSubtype || ''

      if (docTypeFilter === 'Medical Record') {
        return purpose === 'Medical Record'
      }
      if (docTypeFilter.startsWith('subtype:')) {
        const targetSubtype = docTypeFilter.replace('subtype:', '')
        return subtype === targetSubtype
      }
      return purpose === docTypeFilter
    })
  }, [documents, docTypeFilter])
  const canUpdateCase = Permissions.can(user, CAPABILITIES.UPDATE_CASE)
  const canScanOrUpload =
    Permissions.can(user, CAPABILITIES.UPDATE_CASE) || Permissions.can(user, CAPABILITIES.CREATE_CASE)
  const [showCameraScanModal, setShowCameraScanModal] = useState(false)
  const [showHitlModal, setShowHitlModal] = useState(false)
  const [caseModel, setCaseModel] = useState(null)
  const [isAnalyzingSmartUpload, setIsAnalyzingSmartUpload] = useState(false)
  const [smartUploadDocuments, setSmartUploadDocuments] = useState([])
  const [smartUploadExtractedData, setSmartUploadExtractedData] = useState(null)
  const [reviewedDocumentFileId, setReviewedDocumentFileId] = useState(null)
  const smartUploadAbortRef = useRef(null)

  // Upload State
  const [modalOpen, setModalOpen] = useState(false)
  const [pendingFiles, setPendingFiles] = useState([])
  const [pendingFileTotal, setPendingFileTotal] = useState(0)
  const pendingFile = pendingFiles[0] || null

  // Move State
  const [moveModalOpen, setMoveModalOpen] = useState(false)
  const [fileToMove, setFileToMove] = useState(null)

  // Edit State
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [fileToEdit, setFileToEdit] = useState(null)
  const [isUpdatingFileProperties, setIsUpdatingFileProperties] = useState(false)

  // Custom Confirmation Modal State
  const [confirmConfig, setConfirmConfig] = useState({
    show: false,
    title: '',
    message: '',
    files: [],
    confirmText: '',
    confirmVariant: 'primary',
    onConfirm: () => {},
  })

  // Drag and Drop State
  const [isDragging, setIsDragging] = useState(false)
  const [activeDropZone, setActiveDropZone] = useState(null) // 'general' | 'smart'

  const triggerConfirm = ({ title, message, files = [], confirmText, confirmVariant, onConfirm }) => {
    setConfirmConfig({
      show: true,
      title,
      message,
      files,
      confirmText,
      confirmVariant,
      onConfirm: () => {
        onConfirm()
        setConfirmConfig(prev => ({ ...prev, show: false }))
      },
    })
  }

  const handleViewModeChange = mode => {
    setViewMode(mode)
    localStorage.setItem(VIEW_MODE_KEY, mode)
  }

  // Modified to support background refresh (no spinner)
  const fetchDocuments = async (inBackground = false) => {
    try {
      if (!inBackground) setLoading(true)

      CasesService.get(caseNumber)
        .then(setCaseModel)
        .catch(() => {})

      // 1. Fetch Case Files (ShareFile)
      const caseFiles = await sendRequest({
        url: `/api/cases/${caseNumber}/sharefile-files`,
        method: 'GET',
      }).then(response => response.value || response || [])

      // Normalize Case Files
      const normalizedCaseFiles = caseFiles.map(f => ({
        ...f,
        sourceType: 'Case',
        sourceId: caseNumber,
      }))

      // Sort by creation date descending
      const sorted = orderBy(normalizedCaseFiles, ['creationDate', 'uploadedTimeUTC'], ['desc', 'desc'])

      setDocuments(sorted)
      setError(null)

      if (!inBackground) {
        try {
          const searchParams = new URLSearchParams(window.location.search)
          const targetFile = searchParams.get('fileName')
          if (targetFile) {
            const matched = sorted.find(f => f.name && f.name.toLowerCase() === targetFile.toLowerCase())
            if (matched) {
              cogoToast.info(`Viewing shared document: ${matched.name}`)
            }
          }
        } catch {
          // ignore query param parse error
        }
      }
    } catch (err) {
      setError('Failed to load documents.')
    } finally {
      if (!inBackground) setLoading(false)
    }
  }

  const handleOpenHitlReview = async () => {
    try {
      const c = await CasesService.get(caseNumber)
      setCaseModel(c)
      const pending = c?.pendingExtractions || c?.extensions?.pendingExtractions
      if (pending) {
        setSmartUploadExtractedData(pending)
        if (pending.documents && pending.documents.length > 0) {
          const token = getToken()
          const cacheBuster = encodeURIComponent(pending.uploadedAt || pending.jobId || Date.now())
          const enrichedDocs = pending.documents.map((pd, idx) => {
            const existingLocal = smartUploadDocuments.find(
              sd =>
                sd.url &&
                (sd.url.startsWith('data:') || sd.url.startsWith('blob:')) &&
                (sd.fileName === pd.fileName || idx === 0),
            )
            const matchedFile = [...documents]
              .reverse()
              .find(
                f =>
                  f.name === pd.fileName ||
                  f.id === pd.id ||
                  f.id === pd.fileName ||
                  f.displayName === pd.fileName ||
                  f.id === pending.fileId ||
                  (pd.fileName && f.name && f.name.toLowerCase() === pd.fileName.toLowerCase()),
              )
            const realFileName = matchedFile?.name || matchedFile?.displayName || pd.fileName || `Document_${idx + 1}`
            const ext = realFileName ? realFileName.split('.').pop().toLowerCase() : ''
            const isDocPdf = pd.fileType === 'pdf' || ext === 'pdf'
            const isDocImage =
              /^(image|png|jpe?g)$/i.test(pd.fileType || '') || /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(realFileName)
            const isDocWord =
              pd.fileType === 'docx' ||
              pd.fileType === 'doc' ||
              pd.fileType === 'word' ||
              /\.(docx|doc)$/i.test(realFileName)
            const detectedType = isDocPdf ? 'pdf' : isDocImage ? 'image' : isDocWord ? 'docx' : 'txt'
            let resolvedUrl = existingLocal?.url || matchedFile?.url || pd.url || null
            if (!resolvedUrl && (isDocPdf || isDocImage || isDocWord) && realFileName && caseNumber) {
              resolvedUrl = `/api/cases/${caseNumber}/sharefile-files/content/${encodeURIComponent(realFileName)}${
                token ? `?access_token=${token}&t=${cacheBuster}` : `?t=${cacheBuster}`
              }`
            }
            return {
              ...pd,
              id: pd.id || matchedFile?.id || `doc-${idx + 1}`,
              fileName: realFileName,
              fileType: detectedType,
              url: resolvedUrl,
            }
          })
          setSmartUploadDocuments(enrichedDocs)
        }
      } else {
        setSmartUploadExtractedData(null)
      }
      setIsAnalyzingSmartUpload(false)
      setShowHitlModal(true)
    } catch {
      setError('Failed to load case data for review.')
    }
  }

  const handleCancelSmartUpload = async () => {
    if (smartUploadAbortRef.current) {
      smartUploadAbortRef.current.abort()
      smartUploadAbortRef.current = null
    }

    try {
      await sendRequest({
        url: `/api/cases/${caseNumber}/smart-upload/cancel`,
        method: 'POST',
      })
      cogoToast.success('Staged extractions discarded.', { position: 'top-right' })
    } catch {
      // Silently ignore if cancellation completed or already cancelled
    }

    // Clean up local blob URLs
    smartUploadDocuments.forEach(doc => {
      if (doc.url && doc.url.startsWith('blob:')) {
        URL.revokeObjectURL(doc.url)
      }
    })

    // Immediately clear pending extractions from local state so badges clear
    setCaseModel(prev => {
      if (!prev) return prev
      const updated = { ...prev }
      delete updated.pendingExtractions
      if (updated.extensions) {
        const exts = { ...updated.extensions }
        delete exts.pendingExtractions
        delete exts.pending_extractions
        updated.extensions = exts
      }
      return updated
    })

    setIsAnalyzingSmartUpload(false)
    setSmartUploadDocuments([])
    setSmartUploadExtractedData(null)
    setReviewedDocumentFileId(null)
    setShowHitlModal(false)
    fetchDocuments(true)
  }

  const startSmartUpload = async (files, metadata = {}) => {
    if (!files || files.length === 0) return

    // 1. Create local blob preview documents immediately for instant view
    const localDocs = files.map((f, idx) => {
      const rawDisplayName = f.documentName || metadata.name || f.name || ''
      const fileExt = f.name && f.name.includes('.') ? f.name.split('.').pop().toLowerCase() : ''
      const displayName =
        rawDisplayName && !rawDisplayName.includes('.') && fileExt ? `${rawDisplayName}.${fileExt}` : rawDisplayName
      const ext = displayName && displayName.includes('.') ? displayName.split('.').pop().toLowerCase() : fileExt
      const isImg = /^(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(ext) || /^image\//i.test(f.type || '')
      return {
        id: `doc-local-${Date.now()}-${idx + 1}`,
        fileName: displayName,
        fileType: ext === 'pdf' ? 'pdf' : isImg ? 'image' : ext === 'docx' || ext === 'doc' ? 'docx' : 'txt',
        textContent: '',
        url: f.dataUrl || metadata.dataUrl || URL.createObjectURL(f),
        blob: f,
      }
    })

    // 2. Open reviewing experience immediately with analyzing spinner & skeleton cards
    setSmartUploadDocuments(localDocs)
    setIsAnalyzingSmartUpload(true)
    setSmartUploadExtractedData(null)
    setShowHitlModal(true)

    try {
      const c = await CasesService.get(caseNumber)
      if (c) setCaseModel(c)
    } catch {
      // Continue with current caseModel if available
    }

    // 3. Create AbortController for in-flight cancellation
    const abortController = new AbortController()
    smartUploadAbortRef.current = abortController

    // 4. Send upload in background
    try {
      const formData = new FormData()
      files.forEach(f => formData.append('files', f))

      const primary = files[0]
      const docName = metadata.name || primary?.documentName
      const category = metadata.category || primary?.category
      const associatedProviderId = metadata.associatedProviderId || primary?.associatedProviderId
      const associatedProvider =
        metadata.associatedProvider ||
        metadata.associatedProviderName ||
        primary?.associatedProviderName ||
        primary?.associatedProvider
      const documentDate = metadata.documentDate || primary?.documentDate
      const appointmentId = metadata.appointmentId || primary?.appointmentId

      if (docName) formData.append('documentName', docName)
      if (category) formData.append('category', category)
      if (associatedProviderId) formData.append('associatedProviderId', associatedProviderId)
      if (associatedProvider) formData.append('associatedProvider', associatedProvider)
      if (documentDate) formData.append('documentDate', documentDate)
      if (appointmentId) formData.append('appointmentId', appointmentId)

      const response = await sendRequest({
        url: `/api/cases/${caseNumber}/smart-upload`,
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': undefined },
        signal: abortController.signal,
      })

      const targetJobId = response?.jobId ? String(response.jobId).toLowerCase() : null

      // Background job persists files to ShareFile early. Fetch documents after a brief delay so they show in tab.
      setTimeout(() => fetchDocuments(true), 3000)

      // Backend processes Bedrock asynchronously in background (HTTP 202 Accepted).
      // Poll until pendingExtractions is present in the case record or until cancellation.
      // Large records (e.g. 32MB+, 100+ pages) can take 20+ minutes for Textract OCR and Bedrock analysis.
      // We set a 60-minute ceiling with adaptive backoff (2.5s early on, 5s after 1 min, 10s after 5 mins).
      const maxPollDuration = 3600000 // 60 minutes
      const startTime = Date.now()
      let completedPending = null

      if (response?.pendingExtractions?.extractedData || response?.extensions?.pendingExtractions?.extractedData) {
        completedPending = response.pendingExtractions || response.extensions.pendingExtractions
      }

      while (!completedPending && !abortController.signal.aborted && Date.now() - startTime < maxPollDuration) {
        const elapsed = Date.now() - startTime
        const currentInterval = elapsed < 60000 ? 2500 : elapsed < 300000 ? 5000 : 10000
        await new Promise(resolve => setTimeout(resolve, currentInterval))
        if (abortController.signal.aborted) break

        // Keep user session active during long-running background document processing
        window.dispatchEvent(new CustomEvent('session-keep-alive'))

        try {
          const freshCase = await CasesService.get(caseNumber)
          const p = freshCase?.pendingExtractions || freshCase?.extensions?.pendingExtractions
          const matchesJob = targetJobId ? p?.jobId && String(p.jobId).toLowerCase() === targetJobId : true
          const isFresh = p?.uploadedAt ? new Date(p.uploadedAt).getTime() >= startTime - 2000 : true
          if (
            p &&
            matchesJob &&
            isFresh &&
            (p.extractedData || p.diagnosis || p.Diagnosis || p.providers || p.Providers || p.referral || p.injury)
          ) {
            completedPending = p
            setCaseModel(freshCase)
            break
          }
        } catch {
          // Ignore transient poll error
        }
      }

      if (abortController.signal.aborted) return

      if (completedPending) {
        setSmartUploadExtractedData(completedPending)
        if (completedPending.documents && completedPending.documents.length > 0) {
          setSmartUploadDocuments(prev =>
            prev.map((doc, idx) => {
              const matchedServerDoc =
                completedPending.documents[idx] || completedPending.documents.find(d => d.fileName === doc.fileName)
              return {
                ...doc,
                textContent: matchedServerDoc?.textContent || matchedServerDoc?.rawText || doc.textContent,
                pageCount: matchedServerDoc?.pageCount || doc.pageCount,
                lineBlocks: matchedServerDoc?.lineBlocks,
              }
            }),
          )
        }
        setIsAnalyzingSmartUpload(false)
        fetchDocuments(true)
        cogoToast.success('Smart Upload analysis complete! Entities ready for review.', {
          position: 'top-right',
        })
      } else {
        setIsAnalyzingSmartUpload(false)
        setShowHitlModal(false)
        fetchDocuments(true)
        cogoToast.info(
          'Analysis is continuing in the background. Your documents are uploaded; check the Documents tab shortly for pending extractions.',
          { position: 'top-right' },
        )
      }
    } catch (err) {
      if (abortController.signal.aborted) {
        return
      }
      setIsAnalyzingSmartUpload(false)
      setError('Smart Upload analysis failed or was interrupted.')
    } finally {
      smartUploadAbortRef.current = null
    }
  }

  const startSmartReviewForFile = async file => {
    if (!file || !canUpdateCase) return

    const token = getToken()
    const rawName = file.name || file.displayName || ''
    const ext = rawName ? rawName.split('.').pop().toLowerCase() : 'pdf'
    const isDocPdf = ext === 'pdf'
    const isDocImage = /^(image|png|jpe?g|gif|webp|bmp|tiff?)$/i.test(ext)
    const isDocWord = ext === 'docx' || ext === 'doc'
    const detectedType = isDocPdf ? 'pdf' : isDocImage ? 'image' : isDocWord ? 'docx' : 'txt'
    const resolvedUrl =
      (isDocPdf || isDocImage) && rawName
        ? `/api/cases/${caseNumber}/sharefile-files/content/${encodeURIComponent(rawName)}?access_token=${token}`
        : null

    const targetDoc = {
      id: file.id || 'doc-1',
      fileName: rawName,
      fileType: detectedType,
      textContent: '',
      url: resolvedUrl,
    }

    setSmartUploadDocuments([targetDoc])
    setReviewedDocumentFileId(file.id)
    setIsAnalyzingSmartUpload(true)
    setSmartUploadExtractedData(null)
    setShowHitlModal(true)

    try {
      const c = await CasesService.get(caseNumber)
      if (c) setCaseModel(c)
    } catch {
      // Continue with current caseModel if available
    }

    const abortController = new AbortController()
    smartUploadAbortRef.current = abortController

    try {
      const response = await sendRequest({
        url: `/api/cases/${caseNumber}/sharefile-files/${encodeURIComponent(
          file.id,
        )}/smart-review?fileName=${encodeURIComponent(rawName)}`,
        method: 'POST',
        signal: abortController.signal,
      })

      const targetJobId = response?.jobId ? String(response.jobId).toLowerCase() : null

      // Large single-file records (e.g. 32MB+, 100+ pages) can take 20+ minutes for Textract OCR and Bedrock analysis.
      // We set a 60-minute ceiling with adaptive backoff (2.5s early on, 5s after 1 min, 10s after 5 mins).
      const maxPollDuration = 3600000 // 60 minutes
      const startTime = Date.now()
      let completedPending = null

      if (response?.pendingExtractions?.extractedData || response?.extensions?.pendingExtractions?.extractedData) {
        completedPending = response.pendingExtractions || response.extensions.pendingExtractions
      }

      while (!completedPending && !abortController.signal.aborted && Date.now() - startTime < maxPollDuration) {
        const elapsed = Date.now() - startTime
        const currentInterval = elapsed < 60000 ? 2500 : elapsed < 300000 ? 5000 : 10000
        await new Promise(resolve => setTimeout(resolve, currentInterval))
        if (abortController.signal.aborted) break

        // Keep user session active during long-running background document processing
        window.dispatchEvent(new CustomEvent('session-keep-alive'))

        try {
          const freshCase = await CasesService.get(caseNumber)
          const p = freshCase?.pendingExtractions || freshCase?.extensions?.pendingExtractions
          const matchesJob = targetJobId ? p?.jobId && String(p.jobId).toLowerCase() === targetJobId : true
          const isFresh = p?.uploadedAt ? new Date(p.uploadedAt).getTime() >= startTime - 2000 : true
          if (
            p &&
            matchesJob &&
            isFresh &&
            (p.extractedData || p.diagnosis || p.Diagnosis || p.providers || p.Providers || p.referral || p.injury)
          ) {
            completedPending = p
            setCaseModel(freshCase)
            break
          }
        } catch {
          // Ignore transient poll error
        }
      }

      if (abortController.signal.aborted) return

      if (completedPending) {
        setSmartUploadExtractedData(completedPending)
        if (completedPending.documents && completedPending.documents.length > 0) {
          setSmartUploadDocuments(prev =>
            completedPending.documents.map((serverDoc, idx) => {
              const prevDoc = prev[idx] || prev.find(d => d.fileName === serverDoc.fileName)
              return {
                ...serverDoc,
                id: serverDoc.id || prevDoc?.id || file.id || `doc-${idx + 1}`,
                fileName: prevDoc?.fileName || serverDoc.fileName || rawName,
                fileType: prevDoc?.fileType || serverDoc.fileType || detectedType,
                url: prevDoc?.url || resolvedUrl || null,
                textContent: serverDoc.textContent || serverDoc.rawText || prevDoc?.textContent || '',
                pageCount: serverDoc.pageCount || prevDoc?.pageCount,
                lineBlocks: serverDoc.lineBlocks || prevDoc?.lineBlocks || [],
              }
            }),
          )
        }
        setIsAnalyzingSmartUpload(false)
        fetchDocuments(true)
        cogoToast.success(`Smart Review for "${file.name}" complete! Entities ready for review.`, {
          position: 'top-right',
        })
      } else {
        setIsAnalyzingSmartUpload(false)
        setShowHitlModal(false)
        fetchDocuments(true)
        cogoToast.info(
          `Analysis for "${file.name}" is continuing in the background. Check back shortly for pending extractions.`,
          { position: 'top-right' },
        )
      }
    } catch (err) {
      if (abortController.signal.aborted) {
        return
      }
      setIsAnalyzingSmartUpload(false)
      setError('Smart Review analysis failed or was interrupted.')
    } finally {
      smartUploadAbortRef.current = null
    }
  }

  const handleApplyHitlChanges = async acceptedEntities => {
    if (!caseModel) return
    let updatedCase = { ...caseModel }
    acceptedEntities.forEach(ent => {
      if (ent.field === 'Claim Number') {
        updatedCase = {
          ...updatedCase,
          referral: { ...(updatedCase.referral || {}), claimNumber: ent.extractedValue },
        }
      } else if (ent.field === 'Policy Number') {
        updatedCase = {
          ...updatedCase,
          referral: { ...(updatedCase.referral || {}), policyNumber: ent.extractedValue },
        }
      } else if (ent.field === 'Date of Referral') {
        let refDate = ent.extractedValue
        const m = Moment(refDate, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
        if (m.isValid()) {
          refDate = m.format('YYYY-MM-DD')
        }
        updatedCase = {
          ...updatedCase,
          referral: { ...(updatedCase.referral || {}), date: refDate },
        }
      } else if (ent.field === 'Claimant Name' || ent.field === 'Client Name') {
        const parts = (ent.extractedValue || '').trim().split(' ')
        if (parts.length > 0) {
          updatedCase = {
            ...updatedCase,
            client: {
              ...(updatedCase.client || {}),
              firstName: parts[0],
              lastName: parts.length > 1 ? parts.slice(1).join(' ') : updatedCase.client?.lastName,
            },
          }
        }
      } else if (ent.field === 'Date of Birth') {
        updatedCase = {
          ...updatedCase,
          client: { ...(updatedCase.client || {}), birthdate: ent.extractedValue },
        }
      } else if (ent.field === 'Claimant Phone' || ent.field === 'Phone Number') {
        updatedCase = {
          ...updatedCase,
          client: { ...(updatedCase.client || {}), phone: ent.extractedValue },
        }
      } else if (ent.field === 'Claimant Address') {
        const addrObj = ent.attributes || { line1: ent.extractedValue }
        updatedCase = {
          ...updatedCase,
          client: { ...(updatedCase.client || {}), address: addrObj },
        }
      } else if (ent.field === 'Occupation') {
        updatedCase = {
          ...updatedCase,
          client: { ...(updatedCase.client || {}), occupation: ent.extractedValue },
          employer: { ...(updatedCase.employer || {}), occupation: ent.extractedValue },
        }
      } else if (ent.field === 'Date of Injury') {
        updatedCase = {
          ...updatedCase,
          injury: { ...(updatedCase.injury || {}), date: ent.extractedValue },
        }
      } else if (ent.field === 'Mechanism of Injury') {
        const existingDesc = updatedCase.injury?.descriptions || []
        const moiVal = (ent.extractedValue || '').trim()
        const moiDescText = moiVal.toLowerCase().startsWith('mechanism') ? moiVal : `Mechanism of Injury: ${moiVal}`
        const isDuplicate = existingDesc.some(d => {
          const dVal = (typeof d === 'string' ? d : d.description || d.text || '').trim().toLowerCase()
          return (
            dVal === moiDescText.toLowerCase() ||
            dVal === moiVal.toLowerCase() ||
            (dVal.length >= 10 &&
              moiVal.length >= 10 &&
              (dVal.includes(moiVal.toLowerCase()) || moiVal.toLowerCase().includes(dVal)))
          )
        })
        const newDescriptions =
          isDuplicate || !moiVal
            ? existingDesc
            : [...existingDesc, { description: moiDescText, provider: ent.attributes?.provider || 'Report' }]

        updatedCase = {
          ...updatedCase,
          injury: {
            ...(updatedCase.injury || {}),
            moi: ent.extractedValue,
            descriptions: newDescriptions,
          },
        }
      } else if (ent.field === 'Nature of Injury') {
        const existingDesc = updatedCase.injury?.descriptions || []
        const noiVal = (ent.extractedValue || '').trim()
        const noiDescText = noiVal.toLowerCase().startsWith('nature') ? noiVal : `Nature of Injury: ${noiVal}`
        const isDuplicate = existingDesc.some(d => {
          const dVal = (typeof d === 'string' ? d : d.description || d.text || '').trim().toLowerCase()
          return (
            dVal === noiDescText.toLowerCase() ||
            dVal === noiVal.toLowerCase() ||
            (dVal.length >= 10 &&
              noiVal.length >= 10 &&
              (dVal.includes(noiVal.toLowerCase()) || noiVal.toLowerCase().includes(dVal)))
          )
        })
        const newDescriptions =
          isDuplicate || !noiVal
            ? existingDesc
            : [...existingDesc, { description: noiDescText, provider: ent.attributes?.provider || 'Report' }]

        updatedCase = {
          ...updatedCase,
          injury: {
            ...(updatedCase.injury || {}),
            noi: ent.extractedValue,
            descriptions: newDescriptions,
          },
        }
      } else if (ent.field === 'Injury Description') {
        const existingDesc = updatedCase.injury?.descriptions || []
        const newDescVal = (ent.extractedValue || '').trim().toLowerCase()
        const isDuplicate = existingDesc.some(d => {
          const dVal = (typeof d === 'string' ? d : d.description || d.text || '').trim().toLowerCase()
          return (
            dVal === newDescVal ||
            (dVal.length >= 10 && newDescVal.length >= 10 && (dVal.includes(newDescVal) || newDescVal.includes(dVal)))
          )
        })
        if (!isDuplicate) {
          updatedCase = {
            ...updatedCase,
            injury: {
              ...(updatedCase.injury || {}),
              descriptions: [
                ...existingDesc,
                { description: ent.extractedValue, provider: ent.attributes?.provider || 'Report' },
              ],
            },
          }
        }
      } else if (ent.field === 'Employer') {
        updatedCase = {
          ...updatedCase,
          employer: {
            ...(updatedCase.employer || {}),
            name: ent.attributes?.name || ent.extractedValue,
            address: ent.attributes?.address || updatedCase.employer?.address,
            contact: { ...(updatedCase.employer?.contact || {}), phone: ent.attributes?.phone },
            occupation: ent.attributes?.occupation || updatedCase.employer?.occupation,
          },
        }
      } else if (ent.field === 'Date of Hire') {
        const mHire = Moment(ent.extractedValue, ['MM/DD/YYYY', 'YYYY-MM-DD', 'M/D/YYYY', Moment.ISO_8601], true)
        const stdHireDate = mHire.isValid() ? mHire.format('YYYY-MM-DD') : ent.extractedValue
        updatedCase = {
          ...updatedCase,
          employer: {
            ...(updatedCase.employer || {}),
            dateOfHire: stdHireDate,
          },
        }
      } else if (ent.field === 'Insurance Carrier') {
        updatedCase = {
          ...updatedCase,
          insurer: { ...(updatedCase.insurer || {}), name: ent.extractedValue },
          referral: { ...(updatedCase.referral || {}), insuranceCompany: ent.extractedValue },
        }
      } else if (ent.category === 'diagnosis') {
        const existingDiags = updatedCase.diagnosis || []
        const diagVal = ent.extractedValue
        if (diagVal && !existingDiags.some(d => (d.name || d.description || d) === diagVal)) {
          let diagDate = null
          if (ent.attributes?.date) {
            const m = Moment(ent.attributes.date, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
            if (m.isValid()) diagDate = m.format('YYYY-MM-DD')
          }
          updatedCase = {
            ...updatedCase,
            diagnosis: [
              ...existingDiags,
              {
                name: diagVal,
                description: diagVal,
                code: ent.attributes?.code || null,
                anatomicalLevel: ent.attributes?.anatomicalLevel || null,
                category: ent.isPreExisting
                  ? 'Pre-Existing / Incidental'
                  : ent.attributes?.category || (ent.field === 'Primary Diagnosis' ? 'Primary' : 'Secondary'),
                date: diagDate,
                physician: ent.attributes?.physician || null,
              },
            ],
          }
        }
      } else if (ent.category === 'test') {
        const existingTests = updatedCase.tests || []
        const testType = ent.attributes?.type || ent.attributes?.modality || ent.field || 'Diagnostic Test'
        let testDate = ent.attributes?.date || null
        if (testDate) {
          const m = Moment(testDate, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
          testDate = m.isValid() ? m.format('YYYY-MM-DD') : /^\d{4}-\d{2}-\d{2}$/.test(testDate) ? testDate : null
        }
        const testResults = ent.attributes?.results || ent.extractedValue || null
        if (testType) {
          const matchIdx = existingTests.findIndex(
            t => (t.type || t.name || t.modality) === testType && (t.date || null) === testDate,
          )
          if (matchIdx >= 0) {
            const updated = [...existingTests]
            updated[matchIdx] = {
              ...updated[matchIdx],
              type: testType,
              date: testDate || updated[matchIdx].date || null,
              results: testResults,
            }
            updatedCase = { ...updatedCase, tests: updated }
          } else if (
            !existingTests.some(
              t => (t.type || t.name || t.modality) === testType && (t.results || null) === testResults,
            )
          ) {
            updatedCase = {
              ...updatedCase,
              tests: [
                ...existingTests,
                {
                  type: testType,
                  date: testDate || null,
                  results: testResults,
                },
              ],
            }
          }
        }
      } else if (ent.category === 'medication' && ent.field === 'Medication') {
        const existingMeds = updatedCase.medications || []
        const medName = ent.attributes?.name || ent.extractedValue
        if (medName && !existingMeds.some(m => (m.name || m) === medName)) {
          let dateStarted = ent.attributes?.dateStarted || null
          if (dateStarted) {
            const m = Moment(dateStarted, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
            if (m.isValid()) dateStarted = m.format('YYYY-MM-DD')
          }
          let dateDiscontinued = ent.attributes?.dateDiscontinued || null
          if (dateDiscontinued) {
            const m = Moment(dateDiscontinued, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
            if (m.isValid()) dateDiscontinued = m.format('YYYY-MM-DD')
          }
          const relatedToInjury = Boolean(ent.attributes?.relatedToInjury)
          const preInjury = Boolean(ent.attributes?.preInjury || ent.attributes?.isPreInjury)
          const discontinued = Boolean(
            ent.attributes?.discontinued || ent.attributes?.isDiscontinued || dateDiscontinued,
          )
          updatedCase = {
            ...updatedCase,
            medications: [
              ...existingMeds,
              {
                name: medName,
                dosage: ent.attributes?.dosage,
                frequency: ent.attributes?.frequency,
                reason: ent.attributes?.reason || '',
                dateStarted,
                dateDiscontinued,
                pharmacy: ent.attributes?.pharmacy || '',
                relatedToInjury,
                preInjury,
                isPreInjury: preInjury,
                discontinued,
                isDiscontinued: discontinued,
              },
            ],
          }
        }
      } else if (ent.category === 'allergy' || (ent.category === 'medication' && ent.field === 'Allergy')) {
        const existingAllergies = updatedCase.allergies || []
        const algName = ent.attributes?.name || ent.extractedValue
        if (algName && !existingAllergies.some(a => (a.name || a) === algName)) {
          updatedCase = {
            ...updatedCase,
            allergies: [
              ...existingAllergies,
              {
                name: algName,
                reaction: ent.attributes?.reaction,
              },
            ],
          }
        }
      } else if (ent.category === 'appointment') {
        const existingAppts = updatedCase.appointments || []
        if (ent.extractedValue) {
          let startVal = ent.attributes?.date || null
          if (startVal && typeof startVal === 'string') {
            const m = Moment(startVal, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', Moment.ISO_8601], true)
            startVal = m.isValid() ? m.toISOString() : null
          }
          const apptTitle = ent.attributes?.purpose || ent.extractedValue
          if (!existingAppts.some(a => a.title === apptTitle && (a.start || null) === (startVal || null))) {
            updatedCase = {
              ...updatedCase,
              appointments: [
                ...existingAppts,
                {
                  title: apptTitle,
                  description: ent.attributes?.purpose || ent.extractedValue,
                  provider: ent.attributes?.provider || null,
                  start: startVal,
                },
              ],
            }
          }
        }
      } else if (ent.category === 'surgery') {
        const existingSurgeries = updatedCase.surgeries || []
        const existingSurgeriesHosp = updatedCase.surgeriesAndHospitalizations || []
        if (ent.extractedValue) {
          let surgDate = ent.attributes?.date || null
          if (surgDate) {
            const m = Moment(surgDate, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
            if (m.isValid()) surgDate = m.format('YYYY-MM-DD')
          }
          const surgItem = {
            title: ent.attributes?.procedure || ent.extractedValue,
            procedure: ent.attributes?.procedure || ent.extractedValue,
            dateOfSurgery: surgDate,
            date: surgDate,
            facility: ent.attributes?.facility,
            surgeon: ent.attributes?.surgeon,
            provider: ent.attributes?.facility || ent.attributes?.surgeon,
            relatedToInjury: ent.attributes?.relatedToInjury ?? false,
            sourceTextSnippet: ent.sourceSnippet,
          }
          updatedCase = {
            ...updatedCase,
            surgeries: [...existingSurgeries, surgItem],
            surgeriesAndHospitalizations: [...existingSurgeriesHosp, surgItem],
          }
        }
      } else if (ent.category === 'status') {
        if (ent.field === 'Work Status') {
          const cap = ent.attributes?.workCapacity || ent.attributes?.workStatus || ent.extractedValue
          const retStatus = ent.attributes?.returnStatus || ent.attributes?.restrictions || null
          const effDate = ent.attributes?.effectiveDate || ent.attributes?.date || null
          const dlw = ent.attributes?.dateLastWorked || null
          const provider = ent.attributes?.treatmentProvider || null
          const treatment = ent.attributes?.currentTreatment || null
          const isLightDuty = Boolean(
            ent.attributes?.lightDuty || /light|desk|modified/i.test(`${cap || ''} ${retStatus || ''}`),
          )

          const existingStatus = updatedCase.status || {}
          const existingHistory = Array.isArray(existingStatus.history)
            ? [...existingStatus.history]
            : Array.isArray(updatedCase.status)
            ? [...updatedCase.status]
            : []

          if (
            existingHistory.length === 0 &&
            (existingStatus.workStatus || existingStatus.returnStatus || existingStatus.date)
          ) {
            existingHistory.push({
              dateLastWorked: existingStatus.dateLastWorked || null,
              date: existingStatus.date || null,
              treatmentProvider: existingStatus.treatmentProvider || null,
              currentTreatment: existingStatus.currentTreatment || null,
              workStatus: existingStatus.workStatus || null,
              returnStatus: existingStatus.returnStatus || null,
              lightDuty: Boolean(existingStatus.lightDuty),
            })
          }

          const newEntry = {
            dateLastWorked: dlw,
            date: effDate,
            treatmentProvider: provider,
            currentTreatment: treatment,
            workStatus: cap,
            returnStatus: retStatus,
            lightDuty: isLightDuty,
          }

          const existingEntryIdx = existingHistory.findIndex(
            h => h && h.date === effDate && (h.workStatus === cap || (!h.workStatus && !cap)),
          )

          let updatedHistory
          if (existingEntryIdx >= 0) {
            updatedHistory = [...existingHistory]
            updatedHistory[existingEntryIdx] = {
              ...updatedHistory[existingEntryIdx],
              ...newEntry,
            }
          } else {
            updatedHistory = [...existingHistory, newEntry]
          }

          updatedCase = {
            ...updatedCase,
            status: {
              ...existingStatus,
              dateLastWorked: dlw ?? existingStatus.dateLastWorked ?? null,
              workStatus: cap,
              returnStatus: retStatus || existingStatus.returnStatus,
              date: effDate || existingStatus.date,
              treatmentProvider: provider || existingStatus.treatmentProvider,
              currentTreatment: treatment ?? existingStatus.currentTreatment ?? null,
              lightDuty: isLightDuty,
              history: updatedHistory,
            },
            workStatus: {
              ...(updatedCase.workStatus || {}),
              status: cap,
              workCapacity: cap,
              restrictions: retStatus,
              effectiveDate: effDate,
            },
          }
        } else if (ent.field === 'MMI / Impairment') {
          const rawReached = ent.attributes?.isMmiReached ?? ent.attributes?.reached
          const isReached =
            rawReached === true || rawReached === 'true'
              ? true
              : rawReached === false || rawReached === 'false'
              ? false
              : null
          const mmiDateStr = ent.attributes?.mmiDate || ent.attributes?.date || ent.extractedValue
          let stdMmiDate = null
          if (mmiDateStr) {
            const m = Moment(mmiDateStr, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
            if (m.isValid()) stdMmiDate = m.format('YYYY-MM-DD')
            else if (/^\d{4}-\d{2}-\d{2}$/.test(mmiDateStr)) stdMmiDate = mmiDateStr
          }
          const rawRating = ent.attributes?.impairmentRating
          const parsedRating = rawRating ? parseFloat(String(rawRating).replace(/[^0-9.]/g, '')) : null
          let narrative = ent.attributes?.narrative
          if (!narrative) {
            const parts = []
            if (isReached !== null) {
              parts.push(`MMI: ${isReached ? 'Reached' : 'Not Reached'}`)
            } else {
              parts.push('MMI Evaluation')
            }
            if (stdMmiDate) {
              parts.push(`on ${stdMmiDate}`)
            }
            if (Number.isFinite(parsedRating)) {
              parts.push(`• Impairment: ${parsedRating}%`)
            }
            narrative = parts.join(' ')
          }

          const existingMmi = updatedCase.mmi || {}
          const existingHistory = Array.isArray(existingMmi.history) ? [...existingMmi.history] : []

          if (existingHistory.length === 0) {
            const rawExReached =
              typeof existingMmi.reached === 'boolean'
                ? existingMmi.reached
                : typeof existingMmi.isMmiReached === 'boolean'
                ? existingMmi.isMmiReached
                : null
            const exDate = existingMmi.date || existingMmi.mmiDate || null
            if (exDate || rawExReached !== null || existingMmi.impairmentRating) {
              const exParts = []
              if (rawExReached !== null) {
                exParts.push(`MMI: ${rawExReached ? 'Reached' : 'Not Reached'}`)
              } else {
                exParts.push('MMI Evaluation')
              }
              if (exDate) {
                exParts.push(`on ${exDate}`)
              }
              if (existingMmi.impairmentRating) {
                exParts.push(`• Impairment: ${existingMmi.impairmentRating}%`)
              }
              existingHistory.push({
                date: exDate,
                narrative: exParts.join(' '),
                isActual: rawExReached === true,
              })
            }
          }

          const newEntry = {
            date: stdMmiDate,
            narrative,
            isActual: isReached === true,
          }

          const existingEntryIdx = existingHistory.findIndex(
            h => h && h.date === stdMmiDate && (h.isActual === isReached || (!h.date && !stdMmiDate)),
          )

          let updatedHistory
          if (existingEntryIdx >= 0) {
            updatedHistory = [...existingHistory]
            updatedHistory[existingEntryIdx] = {
              ...updatedHistory[existingEntryIdx],
              ...newEntry,
            }
          } else {
            updatedHistory = [...existingHistory, newEntry]
          }

          updatedCase = {
            ...updatedCase,
            mmi: {
              ...existingMmi,
              reached: isReached,
              isMmiReached: isReached,
              date: stdMmiDate || existingMmi.date,
              mmiDate: stdMmiDate || existingMmi.mmiDate,
              impairmentRating: Number.isFinite(parsedRating) ? parsedRating : existingMmi.impairmentRating || null,
              history: updatedHistory,
            },
          }
        }
      }
    })

    // Sequentially process and link providers with parent-child organization hierarchy
    updatedCase = await applyAcceptedProviders(acceptedEntities, updatedCase, caseNumber)

    // Clear pendingExtractions staging extension once changes are reviewed & applied
    delete updatedCase.pendingExtractions
    delete updatedCase.pending_extractions
    delete updatedCase.address

    if (updatedCase.referral?.date) {
      const m = Moment(updatedCase.referral.date, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
      if (m.isValid()) {
        updatedCase.referral.date = m.format('YYYY-MM-DD')
      }
    }

    if (updatedCase.extensions) {
      const exts = { ...updatedCase.extensions }
      delete exts.pendingExtractions
      delete exts.pending_extractions
      updatedCase.extensions = exts
    }

    // Revoke any local blob URLs
    smartUploadDocuments.forEach(doc => {
      if (doc.url && doc.url.startsWith('blob:')) {
        URL.revokeObjectURL(doc.url)
      }
    })

    // Clean empty date strings to null to satisfy JSON schema validation pattern constraints
    const cleanEmptyDates = obj => {
      if (!obj || typeof obj !== 'object') return
      Object.keys(obj).forEach(key => {
        if (obj[key] && typeof obj[key] === 'object') {
          cleanEmptyDates(obj[key])
        } else if (typeof obj[key] === 'string') {
          const lowerKey = key.toLowerCase()
          const isDateField = lowerKey.includes('date') || lowerKey === 'birthdate' || lowerKey === 'dateofhire'
          const isUtcOrTimestamp = lowerKey.endsWith('utc') || lowerKey.endsWith('timestamp')
          if (isDateField && !isUtcOrTimestamp) {
            const trimmed = obj[key].trim()
            if (trimmed === '' || trimmed.toLowerCase() === 'null') {
              obj[key] = null
            } else {
              const m = Moment(trimmed, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
              if (m.isValid()) {
                obj[key] = m.format('YYYY-MM-DD')
              } else if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
                obj[key] = null
              }
            }
          }
        }
      })
    }
    cleanEmptyDates(updatedCase)

    if (Array.isArray(updatedCase.diagnosis)) {
      updatedCase.diagnosis = updatedCase.diagnosis.map(d => {
        if (!d || typeof d !== 'object') return d
        let dDate = d.date
        if (typeof dDate === 'string') {
          const trimmed = dDate.trim()
          if (trimmed === '' || trimmed.toLowerCase() === 'null') {
            dDate = null
          } else {
            const m = Moment(trimmed, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
            dDate = m.isValid() ? m.format('YYYY-MM-DD') : /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null
          }
        }
        return {
          ...d,
          date: dDate ?? null,
        }
      })
    }

    if (Array.isArray(updatedCase.tests)) {
      updatedCase.tests = updatedCase.tests.map(t => {
        if (!t || typeof t !== 'object') return t
        let tDate = t.date
        if (typeof tDate === 'string') {
          const trimmed = tDate.trim()
          if (trimmed === '' || trimmed.toLowerCase() === 'null') {
            tDate = null
          } else {
            const m = Moment(trimmed, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
            tDate = m.isValid() ? m.format('YYYY-MM-DD') : /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null
          }
        }
        return {
          type: t.type || t.modality || t.name || 'Diagnostic Test',
          date: tDate ?? null,
          results: t.results ?? null,
        }
      })
    }

    if (Array.isArray(updatedCase.appointments)) {
      updatedCase.appointments = updatedCase.appointments.map(a => {
        if (!a || typeof a !== 'object') return a
        let startVal = a.start || a.date || null
        if (startVal && typeof startVal === 'string') {
          const m = Moment(startVal, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', Moment.ISO_8601], true)
          startVal = m.isValid() ? m.toISOString() : null
        }
        let endVal = a.end || null
        if (endVal && typeof endVal === 'string') {
          const m = Moment(endVal, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', Moment.ISO_8601], true)
          endVal = m.isValid() ? m.toISOString() : null
        }
        const cleaned = {
          title: a.title || a.purpose || 'Appointment',
          description: a.description || a.purpose || null,
          attendees: a.attendees || null,
          start: startVal,
          end: endVal,
          provider: a.provider || null,
        }
        if (a.location) cleaned.location = a.location
        if (a.transportationServices) cleaned.transportationServices = a.transportationServices
        if (a.translationServices) cleaned.translationServices = a.translationServices
        if (a.confirmations) cleaned.confirmations = a.confirmations
        return cleaned
      })
    }

    // Universal recursive address state normalization
    const STATE_MAP = {
      alabama: 'AL',
      alaska: 'AK',
      arizona: 'AZ',
      arkansas: 'AR',
      california: 'CA',
      colorado: 'CO',
      connecticut: 'CT',
      delaware: 'DE',
      florida: 'FL',
      georgia: 'GA',
      hawaii: 'HI',
      idaho: 'ID',
      illinois: 'IL',
      indiana: 'IN',
      iowa: 'IA',
      kansas: 'KS',
      kentucky: 'KY',
      louisiana: 'LA',
      maine: 'ME',
      maryland: 'MD',
      massachusetts: 'MA',
      michigan: 'MI',
      minnesota: 'MN',
      mississippi: 'MS',
      missouri: 'MO',
      montana: 'MT',
      nebraska: 'NE',
      nevada: 'NV',
      'new hampshire': 'NH',
      'new jersey': 'NJ',
      'new mexico': 'NM',
      'new york': 'NY',
      'north carolina': 'NC',
      'north dakota': 'ND',
      ohio: 'OH',
      oklahoma: 'OK',
      oregon: 'OR',
      pennsylvania: 'PA',
      'rhode island': 'RI',
      'south carolina': 'SC',
      'south dakota': 'SD',
      tennessee: 'TN',
      texas: 'TX',
      utah: 'UT',
      vermont: 'VT',
      virginia: 'VA',
      washington: 'WA',
      'west virginia': 'WV',
      wisconsin: 'WI',
      wyoming: 'WY',
      'district of columbia': 'DC',
      'american samoa': 'AS',
      guam: 'GU',
      'northern mariana islands': 'MP',
      'puerto rico': 'PR',
      'virgin islands': 'VI',
    }
    const normalizeStateValue = st => {
      if (!st || typeof st !== 'string') return st
      const cleaned = st.trim().toLowerCase()
      if (cleaned.length === 2) return st.trim().toUpperCase()
      return STATE_MAP[cleaned] || (cleaned.length === 2 ? cleaned.toUpperCase() : st.trim())
    }
    const normalizeStatesRecursively = obj => {
      if (!obj || typeof obj !== 'object') return
      Object.keys(obj).forEach(key => {
        if (obj[key] && typeof obj[key] === 'object') {
          normalizeStatesRecursively(obj[key])
        } else if (key.toLowerCase() === 'state' && typeof obj[key] === 'string') {
          obj[key] = normalizeStateValue(obj[key])
        }
      })
    }
    normalizeStatesRecursively(updatedCase)

    // Prune blank or invalid items from arrays where schema requires specific properties
    if (Array.isArray(updatedCase.medications)) {
      updatedCase.medications = updatedCase.medications.filter(
        m => m && typeof m.name === 'string' && m.name.trim() !== '',
      )
    }
    if (Array.isArray(updatedCase.allergies)) {
      updatedCase.allergies = updatedCase.allergies.filter(a => a && typeof a.name === 'string' && a.name.trim() !== '')
    }
    if (Array.isArray(updatedCase.treatmentProviders)) {
      updatedCase.treatmentProviders = updatedCase.treatmentProviders.filter(
        p => p && typeof p.name === 'string' && p.name.trim() !== '',
      )
    }
    if (Array.isArray(updatedCase.equipmentProviders)) {
      updatedCase.equipmentProviders = updatedCase.equipmentProviders.filter(
        p => p && typeof p.name === 'string' && p.name.trim() !== '',
      )
    }
    if (Array.isArray(updatedCase.pharmacies)) {
      updatedCase.pharmacies = updatedCase.pharmacies.filter(
        p => p && typeof p.name === 'string' && p.name.trim() !== '',
      )
    }
    if (Array.isArray(updatedCase.equipment)) {
      updatedCase.equipment = updatedCase.equipment.filter(e => e && typeof e.item === 'string' && e.item.trim() !== '')
    }
    if (Array.isArray(updatedCase.tests)) {
      updatedCase.tests = updatedCase.tests.filter(t => t && typeof t.type === 'string' && t.type.trim() !== '')
    }
    if (Array.isArray(updatedCase.homeSetup)) {
      updatedCase.homeSetup = updatedCase.homeSetup.filter(h => h && typeof h.type === 'string' && h.type.trim() !== '')
    }
    if (Array.isArray(updatedCase.diagnosis)) {
      updatedCase.diagnosis = updatedCase.diagnosis.filter(d => d && typeof d.name === 'string' && d.name.trim() !== '')
    }

    // Explicitly purge staged extractions on the backend
    try {
      await CasesService.cancelSmartUpload(caseNumber)
    } catch {}

    updatedCase = sanitizeCaseSystemsAndFunctional(updatedCase)
    const newCaseModel = await CasesService.update(updatedCase)
    const cleanModel = { ...newCaseModel }
    delete cleanModel.pendingExtractions
    delete cleanModel.pending_extractions
    if (cleanModel.extensions) {
      cleanModel.extensions = { ...cleanModel.extensions }
      delete cleanModel.extensions.pendingExtractions
      delete cleanModel.extensions.pending_extractions
    }
    setCaseModel(cleanModel)
    setSmartUploadDocuments([])
    setSmartUploadExtractedData(null)
    setShowHitlModal(false)

    if (reviewedDocumentFileId) {
      try {
        const targetFile = documents.find(d => d.id === reviewedDocumentFileId)
        let existingProps = {}
        if (targetFile?.properties) {
          try {
            existingProps =
              typeof targetFile.properties === 'string' ? JSON.parse(targetFile.properties) : targetFile.properties
          } catch {}
        }
        await sendRequest({
          url: `/api/cases/${caseNumber}/sharefile-files/${encodeURIComponent(reviewedDocumentFileId)}/metadata`,
          method: 'POST',
          data: {
            properties: {
              ...existingProps,
              SmartAiReviewed: 'true',
              SmartAiReviewedAt: new Date().toISOString(),
            },
          },
        })
      } catch {}
      setReviewedDocumentFileId(null)
    }

    // Clean up URL params if present
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.has('review') || searchParams.has('from')) {
      searchParams.delete('review')
      searchParams.delete('from')
      const remaining = searchParams.toString()
      window.history.replaceState({}, '', `${window.location.pathname}${remaining ? `?${remaining}` : ''}`)
    }

    fetchDocuments(true)
  }

  useEffect(() => {
    fetchDocuments()

    // If navigated with ?review=true, auto-open HITL review modal
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('review') === 'true') {
      handleOpenHitlReview()
      searchParams.delete('review')
      const remaining = searchParams.toString()
      window.history.replaceState({}, '', `${window.location.pathname}${remaining ? `?${remaining}` : ''}`)
    }

    const handleNotification = e => {
      const data = e.detail
      if (
        data &&
        (data.title === 'Smart Upload Complete' ||
          data.title === 'Smart Upload Ready for Review' ||
          data.title === 'Smart Document Review Complete' ||
          data.title?.toLowerCase().includes('smart upload') ||
          data.title?.toLowerCase().includes('smart document review'))
      ) {
        const match = data.deepLink?.match(/\/cases\/(\d+)/)
        const notificationCaseNumber = match ? parseInt(match[1], 10) : null
        if (notificationCaseNumber === parseInt(caseNumber, 10)) {
          fetchDocuments(true)
          handleOpenHitlReview()
        }
      }
    }

    window.addEventListener('app-notification', handleNotification)
    return () => {
      window.removeEventListener('app-notification', handleNotification)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseNumber])

  const handleUploadClick = async e => {
    const selectedFiles = Array.from(e.target.files || []).filter(Boolean)
    e.target.value = null // Reset input

    if (selectedFiles.length === 0) return

    setPendingFiles(selectedFiles)
    setPendingFileTotal(selectedFiles.length)
    setModalOpen(true)
  }

  const handleConfirmUpload = async metadata => {
    const file = pendingFile
    const remainingFiles = pendingFiles.slice(1)

    if (!file) {
      setModalOpen(false)
      return
    }

    if (remainingFiles.length === 0) {
      setModalOpen(false)
    }

    try {
      const formData = new FormData()
      formData.append('file', file)

      if (metadata) {
        formData.append('properties', JSON.stringify(metadata))
      }

      await sendRequest({
        url: `/api/cases/${caseNumber}/sharefile-files`,
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': undefined,
        },
      })

      // Refresh silently
      await fetchDocuments(true)
    } catch (err) {
      setError('Failed to upload file.')
    } finally {
      setPendingFiles(remainingFiles)
      if (remainingFiles.length > 0) {
        setModalOpen(true)
      } else {
        setPendingFileTotal(0)
      }
    }
  }

  const handleSmartUploadClick = async e => {
    const files = Array.from(e.target.files)
    e.target.value = null // Reset input

    if (files.length === 0) return
    startSmartUpload(files)
  }

  useEffect(() => {
    const hasFiles = e => {
      if (!e.dataTransfer || !e.dataTransfer.types) return false
      const types = Array.from(e.dataTransfer.types)
      return types.some(t => {
        const lower = String(t).toLowerCase()
        return lower === 'files' || lower === 'application/x-moz-file' || lower === 'downloadurl'
      })
    }

    const handleWindowDragEnter = e => {
      if (!hasFiles(e)) return
      e.preventDefault()
      setIsDragging(true)
    }

    const handleWindowDragOver = e => {
      if (hasFiles(e)) {
        e.preventDefault()
      }
    }

    const handleWindowDragLeave = e => {
      e.preventDefault()
      // Only dismiss when the cursor physically leaves the browser window
      const isOutside =
        e.clientX <= 0 || e.clientY <= 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight

      if (isOutside) {
        setIsDragging(false)
        setActiveDropZone(null)
      }
    }

    const handleWindowDrop = e => {
      e.preventDefault()
      setIsDragging(false)
      setActiveDropZone(null)
    }

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        setIsDragging(false)
        setActiveDropZone(null)
      }
    }

    const handleBlur = () => {
      setIsDragging(false)
      setActiveDropZone(null)
    }

    window.addEventListener('dragenter', handleWindowDragEnter)
    window.addEventListener('dragleave', handleWindowDragLeave)
    window.addEventListener('dragover', handleWindowDragOver)
    window.addEventListener('drop', handleWindowDrop)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('blur', handleBlur)

    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter)
      window.removeEventListener('dragleave', handleWindowDragLeave)
      window.removeEventListener('dragover', handleWindowDragOver)
      window.removeEventListener('drop', handleWindowDrop)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])

  const handleDrop = async (e, zone) => {
    e.preventDefault()
    setIsDragging(false)
    setActiveDropZone(null)

    const files = Array.from(e.dataTransfer?.files || [])
    if (files.length === 0) return

    if (zone === 'smart') {
      startSmartUpload(files)
    } else {
      if (files.length === 1) {
        setPendingFiles([files[0]])
        setPendingFileTotal(1)
        setModalOpen(true)
      } else {
        const performGeneralUpload = async () => {
          try {
            for (const file of files) {
              const formData = new FormData()
              formData.append('file', file)
              await sendRequest({
                url: `/api/cases/${caseNumber}/sharefile-files`,
                method: 'POST',
                data: formData,
                headers: { 'Content-Type': undefined },
              })
            }
            await fetchDocuments(true)
          } catch (err) {
            setError('Failed to upload some files.')
          }
        }

        triggerConfirm({
          title: 'Confirm File Upload',
          message: `You are uploading ${files.length} files directly as attachments to the case folder. This will simply store the files in your case storage but will NOT extract patient details, diagnoses, or provider properties. If you want to automatically extract metadata and merge clinical findings, drag these files into the 'AI Smart Upload' zone instead.`,
          files,
          confirmText: 'Upload Documents',
          onConfirm: performGeneralUpload,
        })
      }
    }
  }

  const handleDeleteFile = async (file, checkOrphan = false) => {
    const performDelete = async () => {
      // OPTIMISTIC UPDATE: Remove immediately from UI
      setDocuments(prev => prev.filter(d => d.id !== file.id))

      try {
        if (file.sourceType === 'Case') {
          // ShareFile Delete
          let url = `/api/cases/${caseNumber}/sharefile-files`
          if (file.id) url += `?id=${file.id}`
          else url += `?fileName=${encodeURIComponent(file.name)}`

          await sendRequest({ url, method: 'DELETE' })
        } else {
          // Appointment/System File Delete (Now Supported)
          await sendRequest({
            url: `/api/cases/${caseNumber}/files/${file.id}`,
            method: 'DELETE',
          })
        }

        // Sync in background just in case
        await fetchDocuments(true)
      } catch (err) {
        alert('Failed to delete file.')
        // Revert optimistic update? For now, we'll just reload.
        await fetchDocuments(true)
      }
    }

    if (checkOrphan) {
      performDelete()
    } else {
      triggerConfirm({
        title: 'Delete Document',
        message: `Are you sure you want to permanently delete "${file.name}"? This action is permanent and cannot be undone.`,
        confirmText: 'Delete',
        confirmVariant: 'danger',
        onConfirm: performDelete,
      })
    }
  }

  const handleMoveClick = file => {
    if (file.sourceType !== 'Case') {
      alert('Only general case files can be moved. Appointment files must be managed within the appointment.')
      return
    }
    setFileToMove(file)
    setMoveModalOpen(true)
  }

  const handleConfirmMove = async targetCaseId => {
    setMoveModalOpen(false)
    if (!fileToMove) return

    try {
      setLoading(true)
      await sendRequest({
        url: `/api/cases/${caseNumber}/sharefile-files/${fileToMove.id}/move`,
        method: 'POST',
        data: { targetCaseNumber: parseInt(targetCaseId) },
      })

      await fetchDocuments()
      setFileToMove(null)
    } catch (err) {
      alert('Failed to move file.')
      setLoading(false)
    }
  }

  const handleEditClick = file => {
    if (file.sourceType !== 'Case') {
      alert('Only general case files can be edited.')
      return
    }
    setFileToEdit(file)
    setEditModalOpen(true)
  }

  const handleConfirmEdit = async metadata => {
    setEditModalOpen(false)
    if (!fileToEdit) return
    const targetFileId = fileToEdit.id

    try {
      setIsUpdatingFileProperties(true)
      await sendRequest({
        url: `/api/cases/${caseNumber}/sharefile-files/${targetFileId}/metadata`,
        method: 'POST',
        data: {
          description: metadata.Description,
          properties: metadata,
        },
      })

      await fetchDocuments(true)
    } catch (err) {
      alert('Failed to update file.')
    } finally {
      setIsUpdatingFileProperties(false)
      setFileToEdit(null)
    }
  }

  const handleViewFile = async file => {
    try {
      const token = getToken()
      let url = ''

      if (file.sourceType === 'Appointment') {
        url = `/api/cases/${caseNumber}/files/${file.id}/${encodeURIComponent(file.name)}?access_token=${token}`
      } else {
        const encodedName = encodeURIComponent(file.name)
        url = `/api/cases/${caseNumber}/sharefile-files/content/${encodedName}?access_token=${token}`
      }

      // Pre-check availability
      try {
        const controller = new AbortController()
        const signal = controller.signal

        const check = await fetch(url, { method: 'GET', signal })

        if (check.status >= 400) {
          // Abort immediately (no need to read body)
          controller.abort()
          throw new Error(`File not found: ${check.status}`)
        }

        // Success: Abort the check request to save bandwidth (we just wanted headers/status)
        // Note: Some browsers might still download a bit, but it prevents full download.
        // We will then open the real window.
        // Actually, for a small 404, we read it. For a large file, we abort.
        controller.abort()

        window.open(url, '_blank')
      } catch (err) {
        if (
          window.confirm(
            'This document appears to be missing from the storage system.\n\nDo you want to delete the record of it from this case?',
          )
        ) {
          await handleDeleteFile(file, true) // true = skip confirmation, we just asked.
        }
      }
    } catch (err) {
      alert('Could not view file.')
    }
  }

  const handleCopyShareLink = async (file, e) => {
    try {
      const isShortLivedTest = Boolean(e && (e.shiftKey || e.altKey))
      const res = await sendRequest({
        url: `/api/cases/${caseNumber}/sharefile-files/share-link`,
        method: 'POST',
        data: {
          fileName: file.name,
          fileId: file.id ? String(file.id) : null,
          expirationHours: 24,
          ...(isShortLivedTest ? { expirationSeconds: 15 } : {}),
        },
      })
      const token = res?.token || res?.Token
      if (!token) {
        throw new Error('No token returned')
      }
      const shareUrl = `${window.location.origin}/shared/file/${token}`
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl)
      }
      cogoToast.success(
        isShortLivedTest
          ? 'Short-lived test link copied to clipboard (expires in 15 seconds).'
          : 'Secure expiring link copied to clipboard (valid for 24 hours).',
      )
    } catch (err) {
      cogoToast.error(err.message || 'Failed to generate secure link.')
    }
  }

  const getIcon = fileName => {
    if (!fileName) return 'file'
    const ext = fileName.split('.').pop().toLowerCase()
    switch (ext) {
      case 'pdf':
        return 'file-pdf'
      case 'doc':
      case 'docx':
        return 'file-word'
      case 'xls':
      case 'xlsx':
        return 'file-excel'
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'file-image'
      case 'txt':
        return 'file-alt'
      default:
        return 'file'
    }
  }

  if (loading && !modalOpen && !moveModalOpen) return <LoadingSpinner />
  if (error) return <div className="alert alert-danger m-3">{error}</div>

  return (
    <div className="case-documents-tab container-fluid mt-3">
      {isDragging && (
        <div
          className="fullscreen-drag-overlay"
          onDragOver={e => {
            e.preventDefault()
            if (e.target === e.currentTarget && activeDropZone !== null) {
              setActiveDropZone(null)
            }
          }}
          onDrop={e => {
            e.preventDefault()
            setIsDragging(false)
            setActiveDropZone(null)
          }}
        >
          <div className="overlay-header">
            <h2>Drop Files to Import</h2>
            <p>Choose your upload style by dragging into a zone below</p>
          </div>
          <div
            className="drop-zones-container"
            onDragOver={e => {
              e.preventDefault()
              if (e.target === e.currentTarget && activeDropZone !== null) {
                setActiveDropZone(null)
              }
            }}
          >
            <div
              className={`drop-zone general-zone ${activeDropZone === 'general' ? 'active' : ''}`}
              onDragEnter={e => {
                e.preventDefault()
                setActiveDropZone('general')
              }}
              onDragOver={e => {
                e.preventDefault()
                if (activeDropZone !== 'general') setActiveDropZone('general')
              }}
              onDrop={e => {
                e.preventDefault()
                handleDrop(e, 'general')
              }}
            >
              <div className="icon-wrapper">
                <FAIcon name="file-upload" />
              </div>
              <h3>Attach to Case</h3>
              <p className="description">Upload your files directly as general attachments to the case folder.</p>
              <span className="action-hint">Drop to Upload</span>
            </div>

            <div
              className={`drop-zone smart-zone ${activeDropZone === 'smart' ? 'active' : ''}`}
              onDragEnter={e => {
                e.preventDefault()
                setActiveDropZone('smart')
              }}
              onDragOver={e => {
                e.preventDefault()
                if (activeDropZone !== 'smart') setActiveDropZone('smart')
              }}
              onDrop={e => {
                e.preventDefault()
                handleDrop(e, 'smart')
              }}
            >
              <div className="icon-wrapper smart-icon-wrapper">
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MagicWand active={true} />
                </div>
              </div>
              <h3>AI Smart Upload</h3>
              <p className="description">
                Extract patient, medical, and document metadata properties using Bedrock AI.
              </p>
              <span className="action-hint">Drop to Extract & Upload</span>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div className="d-flex align-items-center mb-2 mb-sm-0 flex-wrap" style={{ gap: '8px' }}>
          <div className="btn-group">
            <button
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-light text-muted border'}`}
              onClick={() => handleViewModeChange('list')}
              title="List View"
            >
              <FAIcon name="list" />
            </button>
            <button
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-light text-muted border'}`}
              onClick={() => handleViewModeChange('grid')}
              title="Grid View"
            >
              <FAIcon name="th-large" />
            </button>
          </div>
          <Form.Control
            as="select"
            size="sm"
            value={docTypeFilter}
            onChange={e => setDocTypeFilter(e.target.value)}
            style={{ width: 'auto', minWidth: '190px' }}
            title="Filter documents by purpose or subtype"
          >
            <option value="all">All Document Types</option>
            <option value="Medical Record">Medical Record (All)</option>
            {MEDICAL_RECORD_SUBTYPES.map(st => (
              <option key={st.value} value={`subtype:${st.value}`}>
                &nbsp;&nbsp;— {st.label}
              </option>
            ))}
            <option value="Correspondence">Correspondence</option>
            <option value="Legal Document">Legal Document</option>
            <option value="Invoice/Bill">Invoice/Bill</option>
            <option value="Report">Report</option>
            <option value="Initial Case Report">Initial Case Report</option>
            <option value="Case Management Progress Report">Case Management Progress Report</option>
            <option value="Final Case Report">Final Case Report</option>
            <option value="Reviewer CV">Reviewer CV</option>
            <option value="Other">Other</option>
          </Form.Control>
        </div>
        <div>
          {/* Smart Upload Input */}
          <input
            type="file"
            id={`smart-upload-${caseNumber}`}
            style={{ display: 'none' }}
            onChange={handleSmartUploadClick}
            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
            multiple
          />
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="smart-upload-tooltip">
                {caseModel?.pendingExtractions || caseModel?.extensions?.pendingExtractions
                  ? 'Resume pending Smart Upload review'
                  : isAnalyzingSmartUpload
                  ? 'View in-progress Smart Upload analysis'
                  : 'Extract data from a document to add to this case'}
              </Tooltip>
            }
          >
            <button
              className={`btn ${
                caseModel?.pendingExtractions || caseModel?.extensions?.pendingExtractions
                  ? 'btn-info text-white font-weight-bold shadow-sm'
                  : isAnalyzingSmartUpload
                  ? 'btn-outline-info font-weight-bold'
                  : 'btn-outline-primary'
              } mr-2`}
              onClick={() => {
                if (caseModel?.pendingExtractions || caseModel?.extensions?.pendingExtractions) {
                  handleOpenHitlReview()
                } else if (isAnalyzingSmartUpload) {
                  setShowHitlModal(true)
                } else {
                  // Trigger background warmup while user picks file
                  sendRequest({ url: '/api/cases/smart-upload/warmup', method: 'POST' }).catch(() => {})
                  document.getElementById(`smart-upload-${caseNumber}`).click()
                }
              }}
            >
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '5px',
                }}
              >
                <MagicWand active={true} />
              </div>
              <span className="d-none d-sm-inline">Smart Upload</span>
              {(caseModel?.pendingExtractions || caseModel?.extensions?.pendingExtractions) && (
                <span className="badge badge-light text-info ml-1" style={{ fontSize: '0.72rem' }}>
                  Pending Review
                </span>
              )}
              {isAnalyzingSmartUpload && !showHitlModal && (
                <span className="badge badge-info text-white ml-1" style={{ fontSize: '0.72rem' }}>
                  <span
                    className="spinner-border spinner-border-sm mr-1"
                    role="status"
                    style={{ width: '9px', height: '9px', borderWidth: '1.5px' }}
                  />
                  Analyzing
                </span>
              )}
            </button>
          </OverlayTrigger>

          {canScanOrUpload && (
            <button
              type="button"
              className="btn btn-outline-success mr-2 scan-document-btn"
              onClick={() => setShowCameraScanModal(true)}
              title="Scan physical document using device camera"
            >
              <FAIcon name="camera" className="mr-1" />
              <span className="d-none d-sm-inline">Scan Document</span>
              <span className="d-inline d-sm-none">Scan</span>
            </button>
          )}

          <input
            type="file"
            id={`document-upload-${caseNumber}`}
            style={{ display: 'none' }}
            onChange={handleUploadClick}
            multiple
          />
          <button
            className="btn btn-primary"
            onClick={() => document.getElementById(`document-upload-${caseNumber}`).click()}
          >
            <FAIcon name="plus" className="mr-1" />
            <span className="d-none d-sm-inline">Upload Document</span>
            <span className="d-inline d-sm-none">Upload</span>
          </button>
        </div>
      </div>

      {isAnalyzingSmartUpload && !showHitlModal && (
        <div className="alert alert-info d-flex justify-content-between align-items-center mb-3 py-2 px-3 rounded shadow-sm border-info">
          <div>
            <span className="spinner-border spinner-border-sm mr-2 text-info" role="status" aria-hidden="true" />
            <strong>GuideSmart AI Smart Upload in Progress:</strong> Documents are saved to the case. OCR text
            extraction and clinical reasoning are running in the background.
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary bg-white font-weight-bold shadow-sm"
            onClick={() => setShowHitlModal(true)}
          >
            <i className="fas fa-expand-alt mr-1" /> View Progress
          </button>
        </div>
      )}

      {(caseModel?.pendingExtractions || caseModel?.extensions?.pendingExtractions) && (
        <div className="alert alert-info d-flex justify-content-between align-items-center mb-3 py-2 px-3 rounded shadow-sm border-info">
          <div className="d-flex align-items-center mr-3">
            <div
              style={{
                width: '20px',
                height: '20px',
                display: 'inline-block',
                verticalAlign: 'middle',
                marginRight: '8px',
                flexShrink: 0,
              }}
            >
              <MagicWand active={true} />
            </div>
            <div>
              <strong>Smart Upload Extractions Pending Review:</strong> Clinical and case entities were extracted from
              uploaded documents and are staged for confirmation.
            </div>
          </div>
          <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
            <button type="button" className="btn btn-sm btn-primary font-weight-bold" onClick={handleOpenHitlReview}>
              <i className="fas fa-clipboard-check mr-1" /> Review Extractions
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger bg-white"
              onClick={handleCancelSmartUpload}
              title="Discard staged extractions and clear pending badge"
            >
              <i className="fas fa-trash-alt mr-1" /> Discard
            </button>
          </div>
        </div>
      )}

      <div className="position-relative">
        {isUpdatingFileProperties && (
          <div
            className="documents-updating-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.72)',
              zIndex: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backdropFilter: 'blur(2px)',
            }}
          >
            <div className="text-center p-3 bg-white shadow rounded border" style={{ minWidth: '220px' }}>
              <div
                className="spinner-border text-primary mb-2"
                role="status"
                style={{ width: '2rem', height: '2rem' }}
              />
              <div className="small font-weight-bold text-dark">Updating document properties...</div>
            </div>
          </div>
        )}

        {viewMode === 'list' ? (
          <div className="card shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: '26%' }}>Name</th>
                    <th className="col-type" style={{ width: '7%' }}>
                      Type
                    </th>
                    <th className="col-created" style={{ width: '13%' }}>
                      Created Date
                    </th>
                    <th className="col-uploaded" style={{ width: '16%' }}>
                      Uploaded By
                    </th>
                    <th style={{ width: '22%' }}>Details</th>
                    <th style={{ width: '16%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map(file => {
                    let properties = {}
                    try {
                      if (file.properties) properties = JSON.parse(file.properties)
                    } catch (e) {}

                    const isAiReviewed = properties.SmartAiReviewed === 'true' || properties.smartAiReviewed === true

                    return (
                      <tr key={`${file.sourceType}-${file.id}`}>
                        <td>
                          <div className="d-flex align-items-center">
                            <FAIcon name={getIcon(file.name)} className="mr-2 text-muted" fixedWidth />
                            <button
                              className="btn btn-link p-0 text-left"
                              onClick={() => handleViewFile(file)}
                              style={{ maxWidth: '100%', wordBreak: 'break-word', whiteSpace: 'normal' }}
                            >
                              {file.name}
                            </button>
                            {file.hasMultipleVersions && (
                              <span className="badge badge-warning ml-2" title="Has Versions">
                                Versions
                              </span>
                            )}
                          </div>
                          {(file.description || properties.Description) && (
                            <small className="text-muted d-block mt-1">
                              {formatUserDescription(file.description || properties.Description, user)}
                            </small>
                          )}
                        </td>
                        <td className="d-none d-md-table-cell">
                          <span className="text-uppercase text-muted" style={{ fontSize: '0.85rem' }}>
                            {file.name && file.name.includes('.')
                              ? file.name.split('.').pop()
                              : file.fileType || file.type || '-'}
                          </span>
                        </td>
                        <td className="d-none d-md-table-cell">
                          {file.creationDate ? Moment(file.creationDate).format('MM/DD/YYYY h:mm A') : '-'}
                        </td>
                        <td className="d-none d-lg-table-cell">
                          {formatUploadedBy(file.uploadedBy || file.creatorName, user)}
                        </td>
                        <td>
                          {(() => {
                            const hasBadges =
                              isAiReviewed ||
                              properties['Document Purpose'] ||
                              properties['Document Subtype'] ||
                              properties['Record Source'] ||
                              properties.Version

                            if (!hasBadges && file.sourceType !== 'Appointment') {
                              return <span className="text-muted font-italic small">—</span>
                            }

                            return (
                              <>
                                <div className="d-flex flex-wrap align-items-center">
                                  {isAiReviewed && (
                                    <span
                                      className="badge badge-success badge-pill mr-1 mb-1"
                                      title={`Smart AI Review completed${
                                        properties.SmartAiReviewedAt
                                          ? ' on ' + Moment(properties.SmartAiReviewedAt).format('MM/DD/YYYY')
                                          : ''
                                      }`}
                                    >
                                      <FAIcon name="check-circle" className="mr-1" />
                                      AI Reviewed
                                    </span>
                                  )}
                                  {properties['Document Purpose'] && (
                                    <span className="badge badge-info badge-pill mr-1 mb-1">
                                      {properties['Document Purpose']}
                                    </span>
                                  )}
                                  {properties['Document Subtype'] && (
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip id={`doc-subtype-tt-${file.id}`}>
                                          {MEDICAL_RECORD_SUBTYPE_MAP[properties['Document Subtype']] ||
                                            properties['Document Subtype']}
                                        </Tooltip>
                                      }
                                    >
                                      <span
                                        className="badge badge-primary badge-pill mr-1 mb-1"
                                        style={{ cursor: 'help' }}
                                      >
                                        {properties['Document Subtype']}
                                      </span>
                                    </OverlayTrigger>
                                  )}
                                  {properties['Record Source'] && (
                                    <span className="badge badge-secondary badge-pill mr-1 mb-1">
                                      {properties['Record Source']}
                                    </span>
                                  )}
                                  {properties.Version && (
                                    <span className="badge badge-light border badge-pill mb-1">
                                      {properties.Version}
                                    </span>
                                  )}
                                </div>
                                {file.sourceType === 'Appointment' && (
                                  <small className="text-muted d-block mt-1">
                                    Via Appointment: {file.sourceTitle} (
                                    {Moment(file.appointmentDate).format('MM/DD/YYYY')})
                                  </small>
                                )}
                              </>
                            )
                          })()}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <button
                              className="btn btn-sm btn-link text-primary mr-2"
                              title="View"
                              onClick={() => handleViewFile(file)}
                            >
                              <FAIcon name="external-link-alt" />
                            </button>
                            <button
                              className="btn btn-sm btn-link text-primary mr-2"
                              title="Copy secure expiring link (24h — Shift+Click for 15s test link)"
                              onClick={e => handleCopyShareLink(file, e)}
                              data-testid="copy-secure-link-btn"
                            >
                              <FAIcon name="link" />
                            </button>
                            {canUpdateCase && file.sourceType === 'Case' && (
                              <button
                                className={`btn btn-sm btn-link mr-2 btn-smart-review ${
                                  isAiReviewed ? 'text-success' : 'text-purple'
                                }`}
                                title={isAiReviewed ? 'Rerun Smart AI Review' : 'Run Smart AI Review'}
                                onClick={() => startSmartReviewForFile(file)}
                              >
                                <span
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    display: 'inline-block',
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  <MagicWand active={true} />
                                </span>
                              </button>
                            )}
                            {file.sourceType === 'Case' && (
                              <>
                                <button
                                  className="btn btn-sm btn-link text-info mr-2"
                                  title="Edit document properties"
                                  onClick={() => handleEditClick(file)}
                                >
                                  <FAIcon name="pencil-alt" />
                                </button>
                                <button
                                  className="btn btn-sm btn-link text-dark mr-2"
                                  title="Move to another case"
                                  onClick={() => handleMoveClick(file)}
                                >
                                  <FAIcon name="share-square" />
                                </button>
                                <button
                                  className="btn btn-sm btn-link text-danger"
                                  title="Delete"
                                  onClick={() => handleDeleteFile(file)}
                                >
                                  <FAIcon name="trash-alt" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                  {filteredDocuments.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-muted">
                        No documents found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="row">
            {filteredDocuments.map(file => {
              let properties = {}
              try {
                if (file.properties) properties = JSON.parse(file.properties)
              } catch (e) {}

              const isAiReviewed = properties.SmartAiReviewed === 'true' || properties.smartAiReviewed === true

              return (
                <div key={`${file.sourceType}-${file.id}`} className="col-lg-3 col-md-4 col-sm-6 mb-4">
                  <div className="card h-100 shadow-sm library-card-style">
                    {/* Reusing a similar structure to Library Card but adapted for Files */}
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between mb-3">
                        <FAIcon name={getIcon(file.name)} className="fa-2x text-primary" />
                        {file.sourceType === 'Appointment' && (
                          <span className="badge badge-light" title="Appointment Document">
                            Appt
                          </span>
                        )}
                      </div>
                      <h6
                        className="card-title text-truncate py-1 flex-shrink-0"
                        title={file.name}
                        style={{ lineHeight: '1.5', minHeight: '1.5em' }}
                      >
                        {file.name}
                      </h6>

                      {(isAiReviewed ||
                        properties['Document Purpose'] ||
                        properties['Document Subtype'] ||
                        properties['Record Source'] ||
                        properties.Version) && (
                        <div className="mb-2 flex-shrink-0">
                          {isAiReviewed && (
                            <span
                              className="badge badge-success badge-pill mr-1 mb-1"
                              style={{ fontSize: '0.7rem' }}
                              title={`Smart AI Review completed${
                                properties.SmartAiReviewedAt
                                  ? ' on ' + Moment(properties.SmartAiReviewedAt).format('MM/DD/YYYY')
                                  : ''
                              }`}
                            >
                              <FAIcon name="check-circle" className="mr-1" />
                              AI Reviewed
                            </span>
                          )}
                          {properties['Document Purpose'] && (
                            <span className="badge badge-info badge-pill mr-1 mb-1" style={{ fontSize: '0.7rem' }}>
                              {properties['Document Purpose']}
                            </span>
                          )}
                          {properties['Document Subtype'] && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`grid-subtype-tt-${file.id}`}>
                                  {MEDICAL_RECORD_SUBTYPE_MAP[properties['Document Subtype']] ||
                                    properties['Document Subtype']}
                                </Tooltip>
                              }
                            >
                              <span
                                className="badge badge-primary badge-pill mr-1 mb-1"
                                style={{ fontSize: '0.7rem', cursor: 'help' }}
                              >
                                {properties['Document Subtype']}
                              </span>
                            </OverlayTrigger>
                          )}
                          {properties['Record Source'] && (
                            <span className="badge badge-secondary badge-pill mr-1 mb-1" style={{ fontSize: '0.7rem' }}>
                              {properties['Record Source']}
                            </span>
                          )}
                          {properties.Version && (
                            <span className="badge badge-light border badge-pill mb-1" style={{ fontSize: '0.7rem' }}>
                              v{properties.Version}
                            </span>
                          )}
                        </div>
                      )}

                      {(() => {
                        const rawDesc = (
                          file.description ||
                          properties.Description ||
                          properties.description ||
                          ''
                        ).trim()
                        const displayDesc = formatUserDescription(rawDesc, user)

                        return displayDesc ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`desc-tooltip-${file.id}`}>{displayDesc}</Tooltip>}
                          >
                            <p className="card-text small text-muted description-clamp mb-2" style={{ flexShrink: 0 }}>
                              {displayDesc}
                            </p>
                          </OverlayTrigger>
                        ) : (
                          <p className="card-text small text-muted mb-2 font-italic" style={{ flexShrink: 0 }}>
                            No description
                          </p>
                        )
                      })()}

                      <div className="mt-auto pt-2 border-top d-flex justify-content-between align-items-center flex-shrink-0">
                        <small className="text-muted">
                          {file.creationDate ? Moment(file.creationDate).format('MM/DD/YYYY') : ''}
                          {file.uploadedBy && (
                            <span
                              className="d-block text-truncate"
                              style={{ maxWidth: '120px' }}
                              title={formatUploadedBy(file.uploadedBy, user)}
                            >
                              by {formatUploadedBy(file.uploadedBy, user).split(' ')[0]}
                            </span>
                          )}
                        </small>
                        <div className="d-flex align-items-center">
                          <button
                            className="btn btn-sm btn-link text-primary mr-2"
                            title="View in new tab"
                            onClick={() => handleViewFile(file)}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-sm btn-link text-primary mr-2 p-0"
                            title="Copy secure expiring link (24h — Shift+Click for 15s test link)"
                            onClick={e => handleCopyShareLink(file, e)}
                          >
                            <FAIcon name="link" />
                          </button>
                          {canUpdateCase && file.sourceType === 'Case' && (
                            <button
                              className={`btn btn-sm btn-link mr-2 p-0 btn-smart-review ${
                                isAiReviewed ? 'text-success' : 'text-purple'
                              }`}
                              title={isAiReviewed ? 'Rerun Smart AI Review' : 'Run Smart AI Review'}
                              onClick={() => startSmartReviewForFile(file)}
                            >
                              <span
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  display: 'inline-block',
                                  verticalAlign: 'middle',
                                }}
                              >
                                <MagicWand active={true} />
                              </span>
                            </button>
                          )}
                          {file.sourceType === 'Case' && (
                            <button
                              className="btn btn-sm btn-link text-info mr-2 p-0"
                              title="Edit document properties"
                              onClick={() => handleEditClick(file)}
                            >
                              <FAIcon name="pencil-alt" />
                            </button>
                          )}
                          {file.sourceType === 'Case' && (
                            <button
                              className="btn btn-sm btn-link text-dark mr-2 p-0"
                              title="Move to another case"
                              onClick={() => handleMoveClick(file)}
                            >
                              <FAIcon name="share-square" />
                            </button>
                          )}
                          {file.sourceType === 'Case' && (
                            <button
                              className="btn btn-sm btn-link text-danger p-0"
                              title="Delete"
                              onClick={() => handleDeleteFile(file)}
                            >
                              <FAIcon name="trash-alt" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
            {filteredDocuments.length === 0 && <div className="col-12 text-center text-muted">No documents found.</div>}
          </div>
        )}
      </div>

      <FileMetadataModal
        key={pendingFile ? `${pendingFile.name}_${pendingFiles.length}` : 'upload-meta-modal'}
        show={modalOpen}
        title={
          pendingFile
            ? pendingFileTotal > 1
              ? `File Details (${pendingFileTotal - pendingFiles.length + 1} of ${pendingFileTotal}): ${
                  pendingFile.name
                }`
              : `File Details: ${pendingFile.name}`
            : 'File Details'
        }
        onHide={() => {
          const remaining = pendingFiles.slice(1)
          setPendingFiles(remaining)
          if (remaining.length === 0) {
            setModalOpen(false)
            setPendingFileTotal(0)
          }
        }}
        onConfirm={handleConfirmUpload}
      />

      <MoveDocumentModal
        show={moveModalOpen}
        onHide={() => {
          setMoveModalOpen(false)
          setFileToMove(null)
        }}
        onConfirm={handleConfirmMove}
        currentCaseNumber={caseNumber}
      />

      {fileToEdit && (
        <FileMetadataModal
          show={editModalOpen}
          onHide={() => {
            setEditModalOpen(false)
            setFileToEdit(null)
          }}
          onConfirm={handleConfirmEdit}
          title="Edit Document Properties"
          confirmText="Save Changes"
          defaultValues={(() => {
            let parsed = {}
            try {
              if (fileToEdit.properties) {
                parsed =
                  typeof fileToEdit.properties === 'string' ? JSON.parse(fileToEdit.properties) : fileToEdit.properties
              }
            } catch (e) {}
            return {
              purpose: parsed['Document Purpose'] || parsed.documentPurpose || '',
              subtype: parsed['Document Subtype'] || parsed.documentSubtype || '',
              source: parsed['Record Source'] || parsed.recordSource || '',
              version: parsed.Version || parsed.version || '',
              description: formatUserDescription(
                fileToEdit.description || parsed.Description || parsed.description || '',
                user,
              ),
              sequenceNumber:
                parsed['Sequence Number'] || parsed.sequenceNumber || parsed['Progress Report Number'] || '',
              startDate: parsed['Start Date'] || parsed.startDate || parsed.fromDateTimeUtc || '',
              endDate: parsed['End Date'] || parsed.endDate || parsed.toDateTimeUtc || '',
              reportStatus: parsed['Report Status'] || parsed.reportStatus || parsed.status || 'Filed',
            }
          })()}
        />
      )}

      <CustomConfirmModal
        show={confirmConfig.show}
        onHide={() => setConfirmConfig(prev => ({ ...prev, show: false }))}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        files={confirmConfig.files}
        confirmText={confirmConfig.confirmText}
        confirmVariant={confirmConfig.confirmVariant}
      />

      {(caseModel || isAnalyzingSmartUpload) &&
        (() => {
          const isRealPending = obj =>
            obj &&
            typeof obj === 'object' &&
            (obj.extractedData || obj.diagnosis || obj.providers || obj.referral || obj.injury)
          const pendingExtractions = isAnalyzingSmartUpload
            ? null
            : isRealPending(smartUploadExtractedData)
            ? smartUploadExtractedData
            : caseModel?.pendingExtractions || caseModel?.extensions?.pendingExtractions
          const rawDocs =
            smartUploadDocuments.length > 0
              ? smartUploadDocuments
              : pendingExtractions?.documents && pendingExtractions.documents.length > 0
              ? pendingExtractions.documents.map(pd => {
                  const matchedFile = documents.find(
                    f =>
                      f.name === pd.fileName ||
                      f.id === pd.id ||
                      f.id === pd.fileName ||
                      f.displayName === pd.fileName ||
                      (pd.fileName && f.name && f.name.toLowerCase() === pd.fileName.toLowerCase()),
                  )
                  return {
                    ...pd,
                    id: pd.id || matchedFile?.id || `doc-${pd.fileName}`,
                    url: matchedFile?.url || pd.url || null,
                  }
                })
              : documents.map(d => ({
                  id: d.id,
                  fileName: d.displayName || d.name,
                  fileType: d.fileType || 'txt',
                  textContent: d.textContent || '',
                  url: d.url || null,
                }))

          const token = getToken()
          const cacheBuster = encodeURIComponent(
            pendingExtractions?.uploadedAt || pendingExtractions?.jobId || Date.now(),
          )
          const displayDocs = rawDocs.map((doc, idx) => {
            const matchedFile = [...documents]
              .reverse()
              .find(
                f =>
                  f.name === doc.fileName ||
                  f.id === doc.id ||
                  f.id === doc.fileName ||
                  f.displayName === doc.fileName ||
                  f.id === pendingExtractions?.fileId ||
                  (doc.fileName && f.name && f.name.toLowerCase() === doc.fileName.toLowerCase()),
              )
            const rawFileName = doc.fileName || doc.displayName || ''
            const fileName =
              !rawFileName.includes('.') && (matchedFile?.name || matchedFile?.displayName)
                ? matchedFile.name || matchedFile.displayName
                : rawFileName || matchedFile?.name || matchedFile?.displayName || ''
            const ext = fileName ? fileName.split('.').pop().toLowerCase() : ''
            const isDocPdf =
              doc.fileType === 'pdf' ||
              ext === 'pdf' ||
              fileName.toLowerCase().endsWith('.pdf') ||
              matchedFile?.name?.toLowerCase().endsWith('.pdf')
            const isDocImage =
              /^(image|png|jpe?g)$/i.test(doc.fileType || '') ||
              /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(fileName) ||
              /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(matchedFile?.name || '')
            const isDocWord =
              doc.fileType === 'docx' ||
              doc.fileType === 'doc' ||
              doc.fileType === 'word' ||
              /\.(docx|doc)$/i.test(fileName) ||
              /\.(docx|doc)$/i.test(matchedFile?.name || '')
            const detectedType = isDocPdf ? 'pdf' : isDocImage ? 'image' : isDocWord ? 'docx' : 'txt'
            let resolvedUrl = doc.url || doc.blobUrl || matchedFile?.url || null
            if (!resolvedUrl && (isDocPdf || isDocImage || isDocWord) && fileName && caseNumber) {
              resolvedUrl = `/api/cases/${caseNumber}/sharefile-files/content/${encodeURIComponent(fileName)}${
                token ? `?access_token=${token}&t=${cacheBuster}` : `?t=${cacheBuster}`
              }`
            }
            return {
              ...doc,
              id: doc.id || matchedFile?.id || `doc-${idx + 1}`,
              fileName,
              fileType: detectedType,
              url: resolvedUrl,
            }
          })

          return (
            <HitlReviewModal
              show={showHitlModal}
              onHide={() => {
                setShowHitlModal(false)
              }}
              title="Smart Upload"
              isAnalyzing={isAnalyzingSmartUpload}
              onCancelAndDiscard={handleCancelSmartUpload}
              documentData={{
                caseNumber,
                documents: displayDocs,
                fileName:
                  displayDocs[0]?.fileName ||
                  pendingExtractions?.documents?.[0]?.fileName ||
                  documents[0]?.displayName ||
                  `Case_${caseNumber}_Document.txt`,
                fileType: displayDocs[0]?.fileType || 'txt',
                textContent:
                  displayDocs[0]?.textContent ||
                  pendingExtractions?.rawText ||
                  caseModel?.extractedRawText ||
                  (caseModel
                    ? `DOCUMENT INTAKE & SMART UPLOAD RECORD\nCase #${caseNumber}\nClient: ${
                        caseModel.client?.firstName || ''
                      } ${caseModel.client?.lastName || ''}\nClaim #: ${
                        caseModel.referral?.claimNumber || 'None'
                      }\nPolicy #: ${caseModel.referral?.policyNumber || 'None'}\n\nCLINICAL DIAGNOSES:\n${(
                        caseModel.diagnosis || []
                      )
                        .map(d => `- ${formatDiagnosisItem(d)}`)
                        .join('\n')}\n\nEXTRACTED PROVIDERS:\n${(caseModel.providers || [])
                        .map(p => `- ${formatProviderItem(p)}`)
                        .join('\n')}`
                    : ''),
                url: displayDocs[0]?.url || null,
              }}
              rawExtractedData={isAnalyzingSmartUpload ? null : pendingExtractions || caseModel}
              existingCase={caseModel || {}}
              onApplyChanges={handleApplyHitlChanges}
              user={user}
            />
          )
        })()}

      {showCameraScanModal && (
        <CameraScanModal
          show={showCameraScanModal}
          onHide={() => setShowCameraScanModal(false)}
          caseNumber={caseNumber}
          providers={caseModel?.providers || []}
          onUpload={async payload => {
            setShowCameraScanModal(false)
            const filesToUpload =
              Array.isArray(payload?.files) && payload.files.length > 0
                ? payload.files
                : payload?.file
                ? [payload.file]
                : []
            if (filesToUpload.length > 0) {
              filesToUpload.forEach((f, idx) => {
                f.documentName = filesToUpload.length > 1 ? `${payload.name} - Page ${idx + 1}` : payload.name
                f.category = payload.category
                f.documentDate = payload.documentDate
                f.associatedProviderId = payload.associatedProviderId
                f.associatedProviderName = payload.associatedProviderName
                if (payload.appointmentId) {
                  f.appointmentId = payload.appointmentId
                }
              })
              await startSmartUpload(filesToUpload, payload)
            }
          }}
        />
      )}
    </div>
  )
}

export default CaseDocumentsTab
